import {
  portalAcceptedMimeTypes,
  portalFileFormats,
  portalFileFormatForName,
  portalMaxFileBytes,
  type PortalMintUploadBody,
} from "@ledgerbox/domain"

export const uploadInteractionLimit = 20
export const uploadAccept = portalFileFormats.flatMap((format) => format.extensions.map((extension) => `.${extension}`)).join(",")

const canvasBlob = (
  canvas: HTMLCanvasElement,
  type: "image/jpeg" | "image/png",
  quality?: number
): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality))

/** Phone photos are resized before the 25 MiB gate. The browser does not decode
 * HEIC consistently, so those files keep their original bytes. */
export const preparePortalFile = async (file: File): Promise<File> => {
  if (
    file.size <= portalMaxFileBytes ||
    (file.type !== "image/jpeg" && file.type !== "image/png")
  ) {
    return file
  }
  const bitmap = await createImageBitmap(file)
  const longest = Math.max(bitmap.width, bitmap.height)
  const scale = Math.min(1, 2400 / longest)
  const canvas = document.createElement("canvas")
  canvas.width = Math.max(1, Math.round(bitmap.width * scale))
  canvas.height = Math.max(1, Math.round(bitmap.height * scale))
  const context = canvas.getContext("2d")
  if (!context) return file
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close()
  const outputType = file.type === "image/png" ? "image/png" : "image/jpeg"
  const blob = await canvasBlob(
    canvas,
    outputType,
    outputType === "image/jpeg" ? 0.82 : undefined
  )
  if (!blob || blob.size >= file.size) return file
  return new File([blob], file.name, {
    type: outputType,
    lastModified: file.lastModified,
  })
}

export const validatePortalFile = (
  file: File
): { contentType: PortalMintUploadBody["contentType"] } | { error: string } => {
  if (file.size > portalMaxFileBytes) {
    return { error: `${file.name} is larger than 25 MiB.` }
  }
  const format = portalFileFormatForName(file.name)
  // SAFETY: the browser's own MIME string is widened into the accepted union
  // only so `includes` can test it, and it is kept as a content type only
  // when that test passes.
  const browserType = portalAcceptedMimeTypes.includes(
    file.type as PortalMintUploadBody["contentType"]
  )
    ? (file.type as PortalMintUploadBody["contentType"])
    : null
  return format && (!browserType || browserType === format.mimeType)
    ? { contentType: format.mimeType }
    : { error: `${file.name} must use a supported file format and matching file type.` }
}

export const sha256 = async (file: File): Promise<string> => {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer())
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("")
}

export const putFile = (
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress: (progress: number) => void
): Promise<void> =>
  new Promise((resolve, reject) => {
    const upload = new XMLHttpRequest()
    upload.open("PUT", url)
    for (const [name, value] of Object.entries(headers)) {
      upload.setRequestHeader(name, value)
    }
    upload.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total)
    }
    upload.onerror = () => reject(new Error("The file upload was interrupted."))
    upload.onload = () => {
      if (upload.status >= 200 && upload.status < 300) resolve()
      else reject(new Error("The storage service did not accept the file."))
    }
    upload.send(file)
  })

/** One file on its way to the firm. The queue lives in the app shell so a
 * client can switch sections while the phone finishes the upload. */
export type UploadItem = {
  id: string
  file: File
  name: string
  progress: number
  state: "preparing" | "uploading" | "received" | "failed"
  message?: string
  requestId?: string
  requestItemId?: string
}

/**
 * The uploads an open item still reports under its file list. A sent file
 * leaves the queue once the item lists it, so it is not named twice, once
 * as "Received" and once as "Your firm has it" (R5-U4). A file still going,
 * or one that failed, stays until it is sent or dismissed.
 */
export const itemQueueUploads = (
  uploads: readonly UploadItem[],
  itemId: string,
  listed: readonly { fileName: string }[]
): UploadItem[] => {
  const names = new Set(listed.map((file) => file.fileName))
  return uploads.filter(
    (upload) =>
      upload.requestItemId === itemId &&
      !(upload.state === "received" && names.has(upload.name))
  )
}
